import {AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DOCUMENT, Location} from '@angular/common';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {Subscription, timer} from 'rxjs';
import {delay, filter, finalize} from 'rxjs/operators';
import scrollIntoView from 'scroll-into-view-if-needed';

import {
  BODY_HEIGHT_CAL, BODY_HEIGHT_VALUE,
  EXT_HEIGHT_CAL, LIST_SCROLL_INTERVAL,
  MENU_PAGE, PRELOAD_RATIO, SIDE_HEIGHT_DELTA, WORD_SCROLL_SPEED,
} from '../shared/defines/desktop.const';
import {
  BookmarkChange,
  HISTORY_KEY_SEARCH_EXAMPLE,
  HISTORY_KEY_VIEW_EXAMPLE,
  LIST_TYPE,
  SearchParam,
  UI_RELOAD_HEIGHT,
} from '../shared/defines/client.const';
import {generateUrlWithQueryParams} from '../shared/defines/client.func';
import {Example} from '../shared/models/example.model';
import {ExampleSJ} from '../shared/models/example-sj.model';
import {
  ExampleCatIndexItem,
  ExampleCatTree,
  ExamplePostpositionTree,
  findExampleCatIndexItemByKey,
  findExampleCatIndexItemByPostposition,
  getExampleCatIdFromIndexKey,
} from '../shared/models/example-tree.model';
import {PageLoader} from '../shared/interfaces/page-loader';
import {ExampleService} from '../shared/services/example/example.service';
import {ExampleBookmarkService} from '../shared/services/example/example-bookmark.service';
import {SearchBoxComponent} from '../shared/components/search-box/search-box.component';
import {ExampleCatIndexComponent} from '../shared/components/example-cat-index/example-cat-index.component';
import {GlobalEventService} from '../shared/services/global-event/global-event.service';
import {LastIdService} from '../shared/services/last-id/last-id.service';
import {VoiceService} from '../shared/services/voice/voice.service';
import {OptionService} from '../shared/services/option/option.service';

const THIS_PAGE_ADDR = '/example';

type PlayPolicy = 'auto' | 'force' | 'none';

@Component({
  selector: 'app-example',
  templateUrl: './example.component.html',
  styleUrls: ['./example.component.scss']
})
export class ExampleComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('extContainer') extContainer?: ElementRef;
  @ViewChild(SearchBoxComponent) extHeader?: SearchBoxComponent;
  @ViewChild('extBody') extBody?: ElementRef;
  @ViewChild('extBody2') extBody2?: ElementRef;
  @ViewChild('listContainer') listContainer?: ElementRef;
  @ViewChild('innerScroll') innerScroll?: ElementRef;
  @ViewChild('reload') reload?: ElementRef;
  @ViewChild(ExampleCatIndexComponent) catIndex?: ExampleCatIndexComponent;

  MENU_PAGE = MENU_PAGE;
  selectedMenu: MENU_PAGE = MENU_PAGE.EXAMPLE_LISTEN;

  historyKeySearch = HISTORY_KEY_SEARCH_EXAMPLE;
  historyKeyView = HISTORY_KEY_VIEW_EXAMPLE;
  scrollTop = true;

  curDetail: Example | null = null;
  curDetailSJ: ExampleSJ | null = null;
  keyword: string | null = null;

  treeList: Array<ExampleCatTree> = [];
  selectedPostposition: ExamplePostpositionTree | null = null;

  // 목록 로더 (type에 따라 exampleSvc 또는 bmSvc)
  public ll: PageLoader<Example, string> | null = null;
  private type: LIST_TYPE | null = null;

  // 라우터 파라미터
  private queryParams: SearchParam | null = null;
  private queryParams$: Subscription | null = null;
  private routeParams$: Subscription | null = null;
  private router$: Subscription | null = null;

  private bodyHeight = UI_RELOAD_HEIGHT;
  private timer$: Subscription | null = null;
  private tree$: Subscription | null = null;
  private getNextList$: Subscription | null = null;
  private loadDetail$: Subscription | null = null;
  private playExample$: Subscription | null = null;
  private needReloadTimer$: Subscription | null = null;
  private needReload = false;
  private restoreDetailId: string | null = null;
  private get curDetailId(): string | null {
    return this.curDetail?.id ?? null;
  }

  protected showScrollDown = false;
  private scrollDownInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    @Inject(DOCUMENT) private document: Document,
    public exampleSvc: ExampleService,
    public bmSvc: ExampleBookmarkService,
    private globalEventSvc: GlobalEventService,
    private lastIdSvc: LastIdService,
    private voice: VoiceService,
    private option: OptionService,
  ) {
    /**
     * queryParams 구독 — 주소 뒤의 파라미터를 받아오는 곳.
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams.subscribe(params => {
      this.queryParams = {...params};
      console.log('ExampleComponent::queryParams =', this.type, params, this.queryParams);

      if (this.type) {
        if (this.queryParams?.id) {
          // id가 있으면 해당 예문으로 직접 이동
          const id = String(this.queryParams.id);
          console.log('ExampleComponent::queryParams.id =', id);

          const processParams = () => {
            const example = this.ll?.getById(id);
            if (example) {
              this.curDetail = example;
              this.curDetailSJ = example.toExampleSJ();
              this.scrollToExample(id);
            } else {
              this.init();
            }
          };

          if (this.ll?.IsLoading()) {
            this.ll.GetNextList().pipe(
              delay(200),
            ).subscribe(() => processParams());
          } else {
            processParams();
          }
        } else {
          this.init();
        }
      }
    });

    /**
     * route params 구독 — 라우터 주소의 :type 변경 감지.
     */
    this.routeParams$ = route.params.subscribe(params => {
      console.log('ExampleComponent::params =', params, this.queryParams);
      this.type = params['type'];
      this.init();
    });
  }

  private setLastId(detailId: string | null, listId: string | null): void {
    this.lastIdSvc.setLastExampleId(detailId, listId);
  }

  private clearLastId(): void {
    this.lastIdSvc.clearLastExampleId();
  }

  ngOnInit(): void {
    this.router$ = this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
    ).subscribe(() => {
      this.onLeave();
    });
  }

  ngAfterViewInit(): void {
    this.updateExtHeight();

    this.timer$ = timer(2000).subscribe(() => {
      this.updateExtHeight();
    });
  }

  ngOnDestroy(): void {
    this.router$?.unsubscribe();
    this.queryParams$?.unsubscribe();
    this.routeParams$?.unsubscribe();
    this.timer$?.unsubscribe();
    this.tree$?.unsubscribe();
    this.getNextList$?.unsubscribe();
    this.loadDetail$?.unsubscribe();
    this.playExample$?.unsubscribe();
    this.needReloadTimer$?.unsubscribe();
    if (this.scrollDownInterval) {
      clearInterval(this.scrollDownInterval);
      this.scrollDownInterval = null;
    }
    this.voice.Stop();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.updateExtHeight();
  }

  onSearch(param: SearchParam): void {
    console.log('ExampleComponent::onSearch()', param);

    this.router.navigate([THIS_PAGE_ADDR, LIST_TYPE.SERVER], {
      queryParams: param,
    }).then();
  }

  onSelectCat(cat: ExampleCatIndexItem | null): void {
    const queryParams: any = cat ? {cat: cat.key} : {};

    this.router.navigate([THIS_PAGE_ADDR, this.type], {
      queryParams,
    }).then();
  }

  onSelectPostposition(pp: ExamplePostpositionTree | null): void {
    const catKey = this.catIndex?.selectedCat?.key;
    const queryParams: any = pp ? {cat: catKey, pp: pp.id} : (catKey ? {cat: catKey} : {});

    this.router.navigate([THIS_PAGE_ADDR, this.type], {
      queryParams,
    }).then();
  }

  onScroll($event: Event): void {
    if (!this.listContainer || !this.ll || this.ll.IsLoading() || this.ll.IsLastPage()) {
      return;
    }

    if (this.reload) {
      const elContainer = this.listContainer.nativeElement;
      const elReload = this.reload.nativeElement;
      if (elReload.offsetTop < elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight) {
        if (this.getNextList$ === null) {
          this.loadList();
        }
        else {
          this.needReload = true;
        }
      }
    }
  }

  onSelectExample(example: Example, playPolicy: PlayPolicy = 'auto'): void {
    this.curDetail = example;
    this.curDetailSJ = example.toExampleSJ();
    this.loadDetail(example.id, playPolicy);

    // URL에 id 반영
    const queryParams = {
      ...this.queryParams,
      id: example.id,
    };
    // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
    this.router.navigate([THIS_PAGE_ADDR, this.type], {queryParams}).then();
  }

  /******************************************************************************
   * 책갈피 처리
   *****************************************************************************/
  sendingBookmark = false;

  onToggleBookmark($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.curDetail || this.sendingBookmark) {
      return;
    }

    this.sendingBookmark = true;
    this.curDetail.bookmark = !this.curDetail.bookmark;
    this.bmSvc.sendBookmark(this.curDetail.id, this.curDetail.bookmark).pipe(
      finalize(() => this.sendingBookmark = false)
    ).subscribe(
      () => {
        if (this.type === LIST_TYPE.BOOKMARK && this.curDetail && !this.curDetail.bookmark) {
          this.bmSvc.hideBookmark(this.curDetail.id);
          this.curDetail = null;
          this.curDetailSJ = null;
          this.voice.Stop();
        }
      },
      err => console.error(err)
    );
  }

  getBookmarkCss(): string {
    if (this.sendingBookmark) return 'ing';
    return this.curDetail?.bookmark ? 'on' : '';
  }

  private init(): void {
    console.log('ExampleComponent::init()', this.type, this.queryParams);
    const [lastDetailId, lastListId] = this.lastIdSvc.getLastExampleId();
    this.restoreDetailId = null;

    switch (this.type) {
      case LIST_TYPE.SERVER:
        this.selectedMenu = MENU_PAGE.EXAMPLE_LISTEN;
        this.ll = this.exampleSvc;
        this.loadTree();

        // queryParams에서 cat/pp/keyword 복원
        this.keyword = this.queryParams?.keyword ?? null;
        if (this.queryParams?.pp) {
          const ppId = Number(this.queryParams.pp);
          this.findAndSelectPostposition(ppId);
        } else if (this.queryParams?.cat) {
          this.findAndSelectCat(String(this.queryParams.cat));
          this.selectedPostposition = null;
        } else {
          this.selectedPostposition = null;
          this.catIndex?.reset();
        }
        break;
      case LIST_TYPE.BOOKMARK:
        this.selectedMenu = MENU_PAGE.EXAMPLE_LISTEN_BOOKMARK;
        this.ll = this.bmSvc;
        this.keyword = null;
        this.selectedPostposition = null;
        this.catIndex?.reset();
        break;
      default:
        console.error('ExampleComponent: invalid type =', this.type);
        this.ll = this.exampleSvc;
    }

    // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
    // init() must preserve URL `start`; `id` restores the selected detail separately.
    let param: SearchParam = {};
    if (this.queryParams?.pp) {
      param.pp = Number(this.queryParams.pp);
    } else if (this.queryParams?.cat) {
      const catId = getExampleCatIdFromIndexKey(this.queryParams.cat);
      if (catId !== null) {
        param.example_cat = catId;
      } else {
        console.warn(
          `ExampleComponent::init() unknown cat key=${this.queryParams.cat}; example_cat is dropped.`,
          { queryParams: this.queryParams },
        );
      }
    }
    if (this.queryParams?.keyword) {
      param.keyword = this.queryParams.keyword as string;
    }
    if (this.queryParams?.start) {
      param.start = String(this.queryParams.start);
      if (lastListId === param.start && lastDetailId) {
        this.restoreDetailId = String(lastDetailId);
      }
    }
    if (this.queryParams?.id) {
      this.restoreDetailId = String(this.queryParams.id);
      if (!this.queryParams.start) {
        console.warn(
          `ExampleComponent::init() start anchor missing; deriving from id=${this.queryParams.id}.`,
          { queryParams: this.queryParams },
        );
        param.start = String(this.queryParams.id);
      }
    }

    this.reloadList(Object.keys(param).length > 0 ? param : undefined);
  }

  /**
   * treeList에서 postposition ID로 해당 조사를 찾아 선택 상태로 설정한다.
   */
  private findAndSelectPostposition(ppId: number): void {
    const found = findExampleCatIndexItemByPostposition(this.treeList, ppId);
    if (found) {
      this.selectedPostposition = found.postposition;
      this.catIndex?.selectCatItem(found.catItem);
      return;
    }
    // treeList가 아직 로딩 안 됐으면 tree 로딩 후 재시도
    if (this.treeList.length === 0) {
      this.tree$ = this.exampleSvc.GetTree().subscribe(tree => {
        this.treeList = tree;
        this.tree$ = null;
        this.findAndSelectPostposition(ppId);
      });
    }
  }

  /**
   * treeList에서 cat ID로 해당 범주를 찾아 2depth로 진입한다.
   */
  private findAndSelectCat(catKey: string): void {
    const cat = findExampleCatIndexItemByKey(this.treeList, catKey);
    if (cat && this.catIndex) {
      this.catIndex.selectCatItem(cat);
      return;
    }
    // treeList가 아직 로딩 안 됐으면 tree 로딩 후 재시도
    if (this.treeList.length === 0) {
      this.tree$ = this.exampleSvc.GetTree().subscribe(tree => {
        this.treeList = tree;
        this.tree$ = null;
        this.findAndSelectCat(catKey);
      });
    }
  }

  private updateExtHeight(): void {
    if (this.extContainer && this.extHeader) {
      const containerHeight = this.extContainer.nativeElement.offsetHeight;
      const headerHeight = this.extHeader.offsetHeight;
      this.bodyHeight = containerHeight - headerHeight - EXT_HEIGHT_CAL;

      if (this.extBody) {
        this.extBody.nativeElement.style.height = this.bodyHeight + 'px';
      }

      if (this.extBody2) {
        const bodyHeight2 = Math.max(
          BODY_HEIGHT_VALUE - SIDE_HEIGHT_DELTA,
          this.bodyHeight + BODY_HEIGHT_CAL);
        this.extBody2.nativeElement.style.setProperty('--side-height', bodyHeight2 + 'px');
      }
    }
  }

  private loadTree(): void {
    if (this.treeList.length > 0) return; // 이미 로딩됨
    this.tree$ = this.exampleSvc.GetTree().subscribe(tree => {
      this.treeList = tree;
      this.tree$ = null;
    });
  }

  private reloadList(param?: SearchParam): void {
    this.getNextList$?.unsubscribe();
    this.getNextList$ = null;
    this.loadDetail$?.unsubscribe();
    this.loadDetail$ = null;
    this.curDetail = null;
    this.curDetailSJ = null;
    this.needReload = false;
    this.showScrollDown = false;
    this.voice.Stop();

    if (!this.ll) {
      return;
    }

    this.ll.ResetList(param).subscribe(() => {
      this.loadList(true);
    });
  }

  private loadList(cancel = false): void {
    if (this.getNextList$) {
      if (cancel) {
        this.getNextList$.unsubscribe();
      }
      else {
        return;
      }
    }

    if (!this.ll || this.ll.IsLastPage()) {
      return;
    }

    this.getNextList$ = this.ll.GetNextList()
      .pipe(
        finalize(() => {
          this.getNextList$ = null;

          if (this.needReload) {
            this.needReloadTimer$ = timer(0).subscribe(() => {
              this.needReload = false;
              this.loadList();
            });
          }
        })
      ).subscribe(list => {
        if (!this.curDetail && list.length > 0) {
          if (this.restoreDetailId) {
            const id = this.restoreDetailId;
            const example = this.ll?.getById(id);
            if (example) {
              this.curDetail = example;
              this.curDetailSJ = example.toExampleSJ();
              this.loadDetail(example.id);
              if (this.queryParams?.id) {
                timer(100).subscribe(() => this.scrollToExample(id));
              }
              this.updateScrollDown();
              return;
            }
            console.warn(
              `ExampleComponent::loadList() restoreDetailId=${id} not found in loaded list. Falling back to first item.`,
              { listSize: list.length, queryParams: this.queryParams },
            );
          }
          this.curDetail = list[0];
          this.curDetailSJ = list[0].toExampleSJ();
          this.loadDetail(list[0].id);
        }
        this.updateScrollDown();
      });
  }

  private scrollToExample(id: string): void {
    const el = this.document.getElementById(`example-${id}`);
    if (el) {
      scrollIntoView(el, {
        behavior: 'smooth',
        block: 'start',
        scrollMode: 'if-needed',
      });
    } else {
      console.error('ExampleComponent::scrollToExample() - not found element', id);
    }
  }

  private loadDetail(exampleId: string, playPolicy: PlayPolicy = 'none'): void {
    this.loadDetail$?.unsubscribe();
    this.loadDetail$ = this.exampleSvc.GetDetail(exampleId).pipe(
      finalize(() => this.loadDetail$ = null),
    ).subscribe({
      next: detail => {
        this.curDetail = detail;
        this.curDetailSJ = detail.toExampleSJ();

        if (playPolicy === 'force') {
          this.playExample(detail, true);
        }
        else if (playPolicy === 'auto') {
          this.playExample(detail);
        }
      },
      error: err => console.error('ExampleComponent::loadDetail error', err),
    });
  }

  private playExample(example: Example, force = false): void {
    if (!force && !this.option.autoPlay) {
      return;
    }
    if (!example.id || !example.exampleVoice) {
      console.error('ExampleComponent::playExample() missing example voice', example);
      return;
    }

    this.playExample$?.unsubscribe();
    this.playExample$ = this.voice.PlayExample(example.id, example.exampleVoice, 1).pipe(
      finalize(() => this.playExample$ = null),
    ).subscribe({
      error: err => console.error('ExampleComponent::playExample error', err),
    });
  }

  onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;
    this.scrollTop = target.scrollTop === 0;
  }

  onClickRope($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.listContainer?.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  private updateScrollDown(): void {
    timer(0).subscribe(() => {
      if (this.innerScroll && this.listContainer) {
        this.showScrollDown = this.innerScroll.nativeElement.offsetHeight > this.listContainer.nativeElement.offsetHeight;
      }
    });
  }

  onStartScrollDown($event: MouseEvent|TouchEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    this.listContainer?.nativeElement.scrollTo({
      top: this.listContainer.nativeElement.scrollTop + WORD_SCROLL_SPEED * 2,
      behavior: 'auto',
    });

    this.scrollDownInterval = setInterval(() => {
      this.listContainer?.nativeElement.scrollTo({
        top: this.listContainer.nativeElement.scrollTop + WORD_SCROLL_SPEED,
        behavior: 'auto',
      });
    }, LIST_SCROLL_INTERVAL);
  }

  onEndScrollDown($event: MouseEvent|TouchEvent) {
    clearInterval(this.scrollDownInterval!);
    this.scrollDownInterval = null;
  }

  private onLeave(): void {
    this.voice.Stop();

    if (!this.listContainer) {
      return;
    }

    const elContainer = this.listContainer.nativeElement as HTMLElement;
    const children: HTMLElement[] = Array.from(elContainer.querySelectorAll('li[role="option"]'));
    const listItem = children.find((el: HTMLElement) => elContainer.scrollTop <= el.offsetTop);

    const listId = listItem?.id.replace('example-', '');
    if (!listId) {
      this.clearLastId();
      return;
    }

    this.setLastId(this.curDetailId, listId);

    // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
    // Keep `id`; `start` restores list position, `id` restores selected detail.
    const queryParam: SearchParam = {
      ...(this.queryParams ?? {}),
      start: listId,
    };

    const basePath = this.router.url.split('?')[0];
    const urlWithParams = generateUrlWithQueryParams(basePath, queryParam);
    this.location.replaceState(urlWithParams);
  }
}
